import {
  alwaysAddAsReference,
  resetAlwaysAddAsReference,
  setAlwaysAddAsReference,
} from "@/action-dialog/workspaceAddModePreference";
import {
  GroupLabel,
  outlineDestructiveClassName,
  SelectSm,
  SettingRow,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { MicrophonePermissionRequest } from "@/components/MicrophonePermissionRequest";
import { Button } from "@/components/ui/button";
import { SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  CANVAS_GRID_MODES,
  useCanvasGridModePreference,
  type CanvasGridMode,
} from "@/pages/canvas/canvas-controls.types";
import { cn } from "@/utils";
import {
  AudioDevice,
  audioSettingsStorage,
  getAudioInputDevices,
} from "@/utils/audioSettings";
import { useMicrophonePermission } from "@/utils/microphonePermissions";
import { useThemeContext } from "@cohesive/ui-components";
import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

/**
 * Preferences as ONE page. Its scopes stack into a single scrolling pane and
 * the rail is that page's table of contents: a rail click scrolls the pane to
 * the section (reduced motion collapses it to an instant jump), and a
 * scroll-spy walks the active rail row back as you scroll. `scope` stays the
 * one source of the active row — this pane only translates it to and from
 * scroll position via `onScope`.
 *
 * Every control applies instantly; there is no Save.
 */

/** px a section head may sit above the fold and still own the rail. */
const SPY_OFFSET = 24;

function PrefSection({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  return (
    <section data-pref-section={id} className="flex flex-col gap-0.5">
      <GroupLabel>{id}</GroupLabel>
      {children}
    </section>
  );
}

function AppearanceSection() {
  const { preference, setPreference } = useThemeContext();
  const [gridMode, setGridMode] = useCanvasGridModePreference();

  return (
    <PrefSection id="Appearance">
      <SettingRow label="Theme" desc="Follows your system unless you pick one">
        <SelectSm
          value={preference}
          onValueChange={(value) =>
            setPreference(value as "light" | "dark" | "system")
          }
          ariaLabel="Theme"
        >
          <SelectItem value="system">System</SelectItem>
          <SelectItem value="light">Light</SelectItem>
          <SelectItem value="dark">Dark</SelectItem>
        </SelectSm>
      </SettingRow>
      <SettingRow label="Canvas grid" desc="The field behind every canvas">
        <SelectSm
          value={gridMode}
          onValueChange={(value) => setGridMode(value as CanvasGridMode)}
          ariaLabel="Canvas grid"
        >
          {CANVAS_GRID_MODES.map((mode) => (
            <SelectItem key={mode.key} value={mode.key}>
              {mode.name}
            </SelectItem>
          ))}
        </SelectSm>
      </SettingRow>
    </PrefSection>
  );
}

function AudioSection() {
  const { isGranted, checkPermission } = useMicrophonePermission();
  const [devices, setDevices] = useState<AudioDevice[]>([]);
  const [selected, setSelected] = useState<AudioDevice | null>(null);

  const loadDevices = useCallback(async () => {
    try {
      setDevices(await getAudioInputDevices());
    } catch {
      // Nothing actionable to say here — the row just shows no options.
    }
  }, []);

  useEffect(() => {
    setSelected(audioSettingsStorage.getSelectedDevice());
  }, []);

  useEffect(() => {
    if (!isGranted) return;
    void loadDevices();
    // Replaces the old "Refresh device list" button: the browser tells us when
    // a mic is plugged in or pulled out, so the list stays true on its own.
    const media = navigator.mediaDevices;
    media?.addEventListener?.("devicechange", loadDevices);
    return () => media?.removeEventListener?.("devicechange", loadDevices);
  }, [isGranted, loadDevices]);

  return (
    <PrefSection id="Audio">
      {!isGranted ? (
        <MicrophonePermissionRequest
          onPermissionGranted={() => {
            checkPermission();
            toast.success("Microphone access granted.");
          }}
          onPermissionDenied={(result) => toast.error(result.message)}
        />
      ) : (
        <>
          {/* "Granted" tracks the configured device, not the browser grant:
              `AudioRecordingButton` gates dictation on
              `hasConfiguredDevice()`, so clearing it genuinely sends you back
              through microphone setup the next time you dictate. Reading the
              browser permission here instead would leave this row claiming
              "Granted" straight after a revoke. */}
          <SettingRow
            label="Microphone access"
            desc={selected ? "Granted" : "Not granted"}
          >
            {selected ? (
              <Button
                size="sm"
                variant="ghost"
                className={cn(smButtonClassName, outlineDestructiveClassName)}
                onClick={() => {
                  audioSettingsStorage.clear();
                  setSelected(null);
                  toast.success(
                    "Microphone access revoked. You'll be asked to choose a microphone next time you dictate.",
                  );
                }}
              >
                Revoke
              </Button>
            ) : null}
          </SettingRow>
          <SettingRow
            label="Input device"
            desc="Remembered for future recordings"
          >
            <SelectSm
              // `?? ""` rather than `undefined`: dropping to undefined turns
              // the select uncontrolled, so after a revoke it kept displaying
              // the device it no longer holds. An empty value matches no item
              // and falls back to the placeholder.
              value={selected?.deviceId ?? ""}
              // Applies on pick — this pane has no Save, and the old selector's
              // "Save Selection" button was the only thing that persisted a
              // choice, so dropping it without this would have made the
              // dropdown inert.
              onValueChange={(deviceId) => {
                const device = devices.find((d) => d.deviceId === deviceId);
                if (!device) return;
                audioSettingsStorage.setSelectedDevice(device);
                setSelected(device);
              }}
              ariaLabel="Input device"
              placeholder="Select a microphone"
              className="w-56"
            >
              {devices.map((device) => (
                <SelectItem key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </SelectItem>
              ))}
            </SelectSm>
          </SettingRow>
        </>
      )}
    </PrefSection>
  );
}

/**
 * System — the app-level answers that aren't Appearance or Audio. Today that's
 * the workspace add-mode confirmation; it's a rail scope so the group has
 * somewhere to grow.
 *
 * The confirmation is settable in both directions here, not just resettable:
 * it used to be reachable only by first triggering the dialog and ticking
 * "don't ask again", which meant you could silence it by accident and had no
 * way to turn it off deliberately.
 */
function SystemSection() {
  const [always, setAlways] = useState(alwaysAddAsReference);

  return (
    <PrefSection id="System">
      <SettingRow
        label="Add from workspace"
        desc="Files are added as live references, without asking each time."
      >
        <Switch
          checked={always}
          aria-label="Add workspace files as live references without asking"
          onCheckedChange={(checked) => {
            if (checked) setAlwaysAddAsReference();
            else resetAlwaysAddAsReference();
            setAlways(checked);
            toast.success(
              checked
                ? "Workspace files will be added as references."
                : "You'll be asked each time from now on.",
            );
          }}
        />
      </SettingRow>
    </PrefSection>
  );
}

export const PreferencesPane: React.FC<{
  scope: string;
  onScope: (scope: string) => void;
}> = ({ scope, onScope }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  /** A spy-driven scope change must not scroll the pane back. */
  const fromSpy = useRef(false);
  /** While a click-driven scroll is in flight, the spy stands down. */
  const guardUntil = useRef(0);

  const scrollerOf = (root: HTMLElement | null) =>
    root?.closest('[role="tabpanel"]') ?? root?.parentElement ?? null;

  // Rail click (or entry landing) → scroll the pane to the section.
  useEffect(() => {
    if (fromSpy.current) {
      fromSpy.current = false;
      return;
    }
    const root = rootRef.current;
    const scroller = scrollerOf(root);
    const section = root?.querySelector(
      `[data-pref-section="${CSS.escape(scope)}"]`,
    );
    if (!scroller || !section) return;
    guardUntil.current = performance.now() + 700;
    const top =
      scroller.scrollTop +
      section.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    scroller.scrollTo({ top, behavior: reduce ? "auto" : "smooth" });
  }, [scope]);

  // Scroll-spy → the last section head at or above the fold owns the rail; the
  // scroller's floor hands it to the final section outright.
  useEffect(() => {
    const root = rootRef.current;
    const scroller = scrollerOf(root);
    if (!scroller || !root) return;

    const onScroll = () => {
      if (performance.now() < guardUntil.current) return;
      const sections = [
        ...root.querySelectorAll<HTMLElement>("[data-pref-section]"),
      ];
      if (sections.length === 0) return;

      let current = sections[0].dataset.prefSection;
      if (
        Math.ceil(scroller.scrollTop + scroller.clientHeight) >=
        scroller.scrollHeight
      ) {
        current = sections[sections.length - 1].dataset.prefSection;
      } else {
        const fold = scroller.getBoundingClientRect().top + SPY_OFFSET;
        for (const element of sections) {
          if (element.getBoundingClientRect().top <= fold) {
            current = element.dataset.prefSection;
          }
        }
      }

      if (current && current !== scopeRef.current) {
        fromSpy.current = true;
        onScope(current);
      }
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [onScope]);

  return (
    <div ref={rootRef} className="flex max-w-measure flex-col gap-8">
      <AppearanceSection />
      <AudioSection />
      <SystemSection />
    </div>
  );
};
